/**
 * Browser → S3 direct upload via presigned PUT.
 * Falls back to multipart API if CORS/presign fails.
 */

import { compressImageFile } from './imageCompress';
import { petService } from '../api/petService';

export type UploadKind = 'document' | 'pet_photo';

export type DirectUploadOptions = {
  petId: number;
  file: File;
  uploadType: UploadKind;
  documentType?: string | null;
  documentName?: string | null;
  maxRetries?: number;
};

export type DirectUploadResult = {
  success: boolean;
  usedFallback: boolean;
  s3Key?: string;
  documentId?: number;
  imageUrl?: string;
  error?: string;
};

async function sha256Hex(file: File): Promise<string | undefined> {
  try {
    const buffer = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return undefined;
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function putWithRetry(
  url: string,
  file: File,
  headers: Record<string, string>,
  maxRetries: number,
): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        body: file,
        headers,
      });
      if (response.ok) return response;
      lastError = new Error(`S3 PUT failed with status ${response.status}`);
    } catch (err) {
      lastError = err;
    }
    if (attempt < maxRetries - 1) {
      await sleep(1000 * Math.pow(3, attempt)); // 1s, 3s
    }
  }
  throw lastError instanceof Error ? lastError : new Error('S3 PUT failed');
}

async function tryPresignedUpload(
  opts: DirectUploadOptions,
  file: File,
): Promise<DirectUploadResult> {
  const contentHash = await sha256Hex(file);
  const presign = await petService.requestUploadPresign({
    pet_id: opts.petId,
    upload_type: opts.uploadType,
    file_name: file.name,
    content_type: file.type || 'application/octet-stream',
    content_length: file.size,
    document_type: opts.documentType || undefined,
    document_name: opts.documentName || undefined,
  });

  await putWithRetry(
    presign.presigned_url,
    file,
    presign.headers || { 'Content-Type': file.type || 'application/octet-stream' },
    opts.maxRetries ?? 3,
  );

  const complete = await petService.completeUpload({
    pet_id: opts.petId,
    upload_type: opts.uploadType,
    s3_key: presign.s3_key,
    file_name: file.name,
    content_type: file.type || 'application/octet-stream',
    content_length: file.size,
    content_hash: contentHash,
    document_type: opts.documentType || undefined,
    document_name: opts.documentName || undefined,
  });

  return {
    success: true,
    usedFallback: false,
    s3Key: presign.s3_key,
    documentId: complete?.document?.id,
    imageUrl: complete?.image_url,
  };
}

async function fallbackMultipartUpload(
  opts: DirectUploadOptions,
  file: File,
): Promise<DirectUploadResult> {
  if (opts.uploadType === 'pet_photo') {
    const data = await petService.uploadAnimalImages(opts.petId, [file]);
    return {
      success: true,
      usedFallback: true,
      s3Key: data?.images?.[0]?.s3_key,
      imageUrl: data?.images?.[0]?.s3_url,
    };
  }

  const data = await petService.uploadPetDocuments(
    opts.petId,
    [file],
    opts.documentType || null,
    opts.documentName || null,
  );
  return {
    success: true,
    usedFallback: true,
    s3Key: data?.documents?.[0]?.s3_key,
    documentId: data?.documents?.[0]?.id,
  };
}

/**
 * Compress (if image) → presign → PUT → complete.
 * On CORS / TypeError / presign failure, fall back to multipart API.
 */
export async function uploadFileToPet(opts: DirectUploadOptions): Promise<DirectUploadResult> {
  const prepared = await compressImageFile(opts.file);

  try {
    return await tryPresignedUpload(opts, prepared);
  } catch (err: any) {
    const message = err?.message || String(err);
    const isCorsOrNetwork =
      err instanceof TypeError ||
      /Failed to fetch|NetworkError|CORS|network/i.test(message);

    console.warn('[directS3Upload] presigned path failed, trying multipart fallback:', message);

    if (!isCorsOrNetwork && err?.response?.status && err.response.status < 500) {
      // Auth / validation errors — don't mask with fallback
      return { success: false, usedFallback: false, error: message };
    }

    try {
      return await fallbackMultipartUpload(opts, prepared);
    } catch (fallbackErr: any) {
      return {
        success: false,
        usedFallback: true,
        error: fallbackErr?.message || message || 'Upload failed',
      };
    }
  }
}
